import React from 'react';
import { Typography, Box, Stack } from '@mui/material';
import ApexCharts from 'react-apexcharts';
import palette from 'src/theme/palette';
import * as yup from 'yup';

interface DonutChartData {
  label?: string;
  data: number[];
  labels: string[];
  colors: string[];
  showHeader?: boolean;
  height?: number;
}

const donutChartSchema = yup.object().shape({
  label: yup.string().optional(),
  data: yup.array().of(yup.number().min(0)).required('Data array is required'),
  labels: yup.array().of(yup.string()).required('Labels array is required'),
  colors: yup.array().of(yup.string()).required('Colors array is required'),
});

const DEFAULT_DATA = {
  data: [0, 0],
  labels: ['Highlights', 'Challenges'],
  colors: [palette.primary.main, palette.error.main],
};

function DonutChart({
  label,
  data: inputData,
  labels: inputLabels,
  colors: inputColors,
  showHeader = false,
  height = 200,
}: DonutChartData) {
  const data = inputData?.length ? inputData : DEFAULT_DATA.data;
  const labels = inputLabels?.length ? inputLabels : DEFAULT_DATA.labels;
  const colors = inputColors?.length ? inputColors : DEFAULT_DATA.colors;

  try {
    donutChartSchema.validateSync({ label, data, labels, colors });
  } catch (error) {
    console.error('DonutChart validation error:', error);
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error" variant="caption">
          Failed to render chart due to invalid data
        </Typography>
      </Box>
    );
  }

  // Calculate total and percentages
  const total = data.reduce((acc, curr) => acc + (isNaN(curr) ? 0 : curr), 0);
  const isZeroData = total === 0;

  const percentages = data.map((value) => {
    if (isNaN(value) || total === 0) return 0;
    return Math.round((value / total) * 100);
  });

  // When total is 0, render a neutral gray ring
  const chartSeries = isZeroData ? [1] : data;
  const chartColors = isZeroData ? ['#E2E8F0'] : colors;

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'donut',
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    labels: isZeroData ? ['No Activity'] : labels,
    colors: chartColors,
    stroke: {
      width: 2,
      colors: ['#FFFFFF'],
    },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '11px',
              fontWeight: 600,
              color: '#64748B',
              offsetY: -4,
            },
            value: {
              show: true,
              fontSize: '18px',
              fontWeight: 700,
              color: isZeroData ? '#94A3B8' : '#1E293B',
              offsetY: 2,
              formatter: () => (isZeroData ? '0' : `${total}`),
            },
            total: {
              show: true,
              label: isZeroData ? 'No Data' : 'Total',
              fontSize: '11px',
              fontWeight: 600,
              color: '#64748B',
              formatter: () => (isZeroData ? '0' : `${total}`),
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: !isZeroData,
      y: {
        formatter: (val) => `${val} logged`,
      },
    },
    legend: {
      show: false,
    },
    states: {
      hover: {
        filter: isZeroData ? { type: 'none' } : { type: 'lighten', value: 0.15 },
      },
      active: {
        filter: isZeroData ? { type: 'none' } : { type: 'darken', value: 0.2 },
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {showHeader && label && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
        </Box>
      )}

      {/* Donut Visualization */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 0.5 }}>
        <ApexCharts options={options} series={chartSeries} type="donut" height={height} width="100%" />
      </Box>

      {/* Legend & Breakdown with Counts and Percentages */}
      <Stack spacing={1} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        {labels.map((itemLabel, index) => {
          const count = isNaN(data[index]) ? 0 : data[index];
          const pct = percentages[index];
          const color = isZeroData ? '#94A3B8' : colors[index] || palette.grey[500];

          return (
            <Stack
              key={`${itemLabel}-${index}`}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: isZeroData ? 'grey.400' : colors[index] || 'grey.500',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'text.primary',
                  }}
                >
                  {itemLabel}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: isZeroData ? 'text.secondary' : 'text.primary',
                  }}
                >
                  {count}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                  }}
                >
                  ({pct}%)
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

export default DonutChart;

